/*
 * Lemonade Training Oy copy rights all right reserved.
 * LT-2034: refund audit records, the gym's "needs attention" queue and the
 * owner's visit-card adjustment.
 */

import { Timestamp } from "firebase/firestore";

/* -------------- [ StripeRefundEvents/{chargeId | disputeId} ] ------------------ */

/**
 * Why a fully refunded purchase is still waiting on a person. Absent on a
 * record whose revocation completed or that never needed one.
 * - stacked_visit_card: the card also holds other purchases and the refunded
 *   visits could not be worked out (legacy: no ledger, or an unresolvable line item).
 * - unfulfilled_session: the checkout never finished provisioning; what landed is unknown.
 * - product_missing: the product or the member's card is gone.
 * - foreign_product: a line item belongs to another Stripe account.
 * - revocation_failed: a transient failure Stripe will retry.
 */
export const REFUND_ATTENTION_REASONS = [
  "stacked_visit_card",
  "unfulfilled_session",
  "product_missing",
  "foreign_product",
  "revocation_failed",
] as const;
export type RefundAttentionReason = (typeof REFUND_ATTENTION_REASONS)[number];

export const REFUND_RESOLUTIONS = ["adjusted", "kept"] as const;
/** adjusted: the owner changed the card; kept: the owner chose to leave access as is. */
export type RefundResolution = (typeof REFUND_RESOLUTIONS)[number];

/** Server-written audit record; Stripe ids and amounts only, never names or card data. */
export type StripeRefundEvent = {
  kind: "refund" | "dispute";
  chargeId?: string;
  disputeId?: string;
  paymentIntentId?: string | null;
  /** Connected account id, or null for a platform charge. */
  connectedAccount: string | null;
  amount: number;
  amountRefunded?: number;
  currency: string;
  /** Full refund of the charge. */
  refunded?: boolean;
  /** Disputes only: Stripe's dispute reason and status as retrieved. */
  reason?: string | null;
  status?: string | null;
  uid: string | null;
  sessionId?: string | null;
  updatedAt: Timestamp | Date;
  /** Moves forward only: true once every entitlement was revoked. */
  accessRevoked?: boolean;
  /** Money went back but access needs a person; the queryable marker for the attention queue. */
  revocationPending?: boolean;
  manualReviewRequired?: boolean;
  manualReviewProducts?: string[];
  foreignProductsSkipped?: string[];
  permanentFailedProducts?: string[];
  unfulfilledSession?: boolean;
  revocationFailedAt?: Timestamp | Date;
  /** Visits taken off a stacked card for this refund (LT-2034). */
  visitsRevoked?: number;
  /**
   * Visits of the refunded purchase the member had already used when the
   * refund landed, so the card was floored at zero instead of going negative.
   */
  visitsOverdrawn?: number;
  /** Set by the owner's resolution on the dashboard, never by the webhook. */
  resolvedAt?: Timestamp | Date;
  resolvedByRole?: "owner" | "admin";
  resolution?: RefundResolution;
};

/* -------------- [ Boxes/{boxID}/VisitCardAdjustments/{auto} ] ------------------ */

/** Append-only trail of every owner adjustment to a member's visit card; admins read, no client writes. */
export type VisitCardAdjustment = {
  uid: string;
  productUID: string;
  /** Attribution by role only; the actor uid is server-side in the log. */
  actorRole: "owner" | "admin";
  before: { visitsTotal: number; visitsRemaining: number };
  after: { visitsTotal: number; visitsRemaining: number };
  /** The refund this adjustment resolved, when it was made from the attention queue. */
  chargeId: string | null;
  createdAt: Timestamp | Date;
};

/* -------------- [ Callables ] ------------------ */

/** Pending refunds read per gym at most; above it `capped` is true. */
export const REFUND_ATTENTION_MAX = 50;

export type GetRefundsNeedingAttentionRequest = { boxID: string };

export type RefundAttentionCard = {
  status: string;
  visitsTotal: number;
  visitsRemaining: number;
  visitsUsed: number;
};

export type RefundAttentionItem = {
  chargeId: string;
  uid: string;
  /** "" when the account has no name. */
  memberName: string;
  productID: string | null;
  /** The product's name, or "" when the product is gone. */
  productName: string;
  /** Minor units, as Stripe reports them. */
  amountRefunded: number;
  currency: string;
  /** ISO 8601 instant of the refund record's last update. */
  refundedAt: string;
  reason: RefundAttentionReason;
  /** Visits already used from the refunded purchase; only with a floored card. */
  visitsOverdrawn: number | null;
  /** The member's card for the product, or null when there is none. */
  card: RefundAttentionCard | null;
};

export type GetRefundsNeedingAttentionResponse = {
  items: RefundAttentionItem[];
  capped: boolean;
};

export const VISIT_CARD_ADJUST_MAX_VISITS = 1000;

/**
 * Sets the member's remaining visits on one card; `visitsTotal` moves by the
 * same delta so the used count stays true. Zero remaining leaves the card
 * active and spent, exactly like a card used up in class.
 */
export type AdjustVisitCardVisitsRequest = {
  boxID: string;
  uid: string;
  productID: string;
  /** 0..VISIT_CARD_ADJUST_MAX_VISITS. */
  visitsRemaining: number;
  /** When set, the matching refund record is marked resolved as "adjusted". */
  chargeId?: string;
};

export type AdjustVisitCardVisitsResponse = {
  visitsTotal: number;
  visitsRemaining: number;
};

/** The owner keeps the member's access as it is; the record leaves the queue. */
export type ResolveRefundReviewRequest = { boxID: string; chargeId: string };

export type ResolveRefundReviewResponse = { resolution: RefundResolution };
